import { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, Send, Loader2, Check } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from '../context/LanguageContext';
import { useConversations } from '../lib/queries';
import { sendMessage } from '../lib/api';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

/**
 * Reusable bottom-sheet modal that lets the user send a "share"
 * payload (recipe or workout) to one of their existing accepted
 * conversations.
 *
 * To keep the MVP simple we don't let the user start a brand-new chat
 * from here — they have to pick from the inbox they already have.
 * Sending to a brand-new contact remains the job of the per-profile
 * "Send message" button.
 *
 * Props:
 *   type:      'recipe_share' | 'workout_share'
 *   data:      snapshot object stored alongside the message
 *   caption:   optional first line of the bubble text
 */
export default function SendToChatModal({ isOpen, onClose, type, data, caption = '' }) {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const qc = useQueryClient();

  const [query, setQuery] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [sent, setSent] = useState(new Set());

  // Pull the inbox so the user can pick a partner. Polling is disabled
  // here — the modal lifetime is short.
  const { data: convs = [], isLoading } = useConversations('inbox', {
    enabled: isOpen,
    refetchInterval: false,
  });

  // Only accepted convs make sense as share targets.
  const targets = useMemo(
    () => (convs || []).filter((c) => c.status === 'accepted' && c.other_user),
    [convs]
  );
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return targets;
    return targets.filter((c) => {
      const u = c.other_user || {};
      const hay = `${u.name || ''} ${u.username || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [targets, query]);

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setSent(new Set());
      setBusyId(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSend = async (conv) => {
    const recipientId = conv.other_user?.user_id;
    if (!recipientId || busyId) return;
    setBusyId(conv.conversation_id);
    try {
      await sendMessage({
        recipient_id: recipientId,
        type,
        text: caption || '',
        data,
      });
      setSent((s) => new Set([...s, conv.conversation_id]));
      toast.success(t('messages.shareSent', 'Inviato'));
      qc.invalidateQueries({ queryKey: ['messages'] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    } finally {
      setBusyId(null);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[300] bg-black/60 backdrop-blur-sm flex items-end justify-center"
      onClick={onClose}
      data-testid="send-to-chat-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`w-full max-w-md rounded-t-3xl flex flex-col ${
          isDark ? 'bg-[#0D1424]' : 'bg-white'
        }`}
        style={{
          maxHeight: '85vh',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
        data-testid="send-to-chat-modal"
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between px-5 py-4 border-b ${
            isDark ? 'border-white/10' : 'border-gray-100'
          }`}
        >
          <h3 className={`font-display text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {t('messages.sendToChat', 'Invia in chat')}
          </h3>
          <button
            onClick={onClose}
            data-testid="send-to-chat-close"
            className={isDark ? 'text-white/60' : 'text-gray-400'}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search */}
        <div className={`px-4 py-3 border-b ${isDark ? 'border-white/10' : 'border-gray-100'}`}>
          <div
            className={`flex items-center gap-2 px-3 py-2 rounded-xl ${
              isDark ? 'bg-white/5' : 'bg-gray-100'
            }`}
          >
            <Search className={`w-4 h-4 ${isDark ? 'text-white/50' : 'text-gray-400'}`} />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('messages.searchUsers', 'Cerca…')}
              data-testid="send-to-chat-search"
              className={`flex-1 bg-transparent text-sm focus:outline-none ${
                isDark ? 'text-white placeholder-white/40' : 'text-gray-900 placeholder-gray-400'
              }`}
            />
          </div>
        </div>

        {/* Conversation list */}
        <div className="flex-1 overflow-y-auto px-4 py-2">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-[#E8334A]" />
            </div>
          ) : filtered.length === 0 ? (
            <div
              className={`text-center py-12 ${isDark ? 'text-white/40' : 'text-gray-400'}`}
              data-testid="send-to-chat-empty"
            >
              <p className="text-sm">
                {targets.length === 0
                  ? t('messages.shareNoConvs', 'Nessuna conversazione disponibile')
                  : t('messages.shareNoMatches', 'Nessun risultato')}
              </p>
            </div>
          ) : (
            <div className="space-y-1">
              {filtered.map((c) => {
                const peer = c.other_user || {};
                const peerName = peer.name || peer.username || '…';
                const isSent = sent.has(c.conversation_id);
                const isBusy = busyId === c.conversation_id;
                return (
                  <button
                    key={c.conversation_id}
                    onClick={() => !isSent && handleSend(c)}
                    disabled={isSent || isBusy}
                    data-testid={`send-to-chat-row-${c.conversation_id}`}
                    className={`w-full flex items-center gap-3 p-2.5 rounded-xl text-left transition-colors ${
                      isSent
                        ? 'opacity-60'
                        : isDark
                        ? 'hover:bg-white/5'
                        : 'hover:bg-gray-100'
                    }`}
                  >
                    {peer.picture ? (
                      <img
                        src={peer.picture}
                        alt={peerName}
                        className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                      />
                    ) : (
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm ${
                          isDark ? 'bg-white/10 text-white' : 'bg-gray-200 text-gray-700'
                        }`}
                      >
                        {peerName.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p
                        className={`font-semibold text-sm truncate ${
                          isDark ? 'text-white' : 'text-gray-900'
                        }`}
                      >
                        {peerName}
                      </p>
                      {peer.username && peer.name && (
                        <p className={`text-xs truncate ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
                          @{peer.username}
                        </p>
                      )}
                    </div>
                    {isBusy ? (
                      <Loader2 className="w-5 h-5 animate-spin text-[#E8334A]" />
                    ) : isSent ? (
                      <Check className="w-5 h-5 text-green-500" />
                    ) : (
                      <Send className="w-4 h-4 text-[#E8334A]" />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
